import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import useCustomFields from '../hooks/useCustomFields';
import useResearchAccess from '../hooks/useResearchAccess';
import ResearchLockedNotice from './ResearchLockedNotice';
import {
  AUTH_TYPES,
  COLUMN_TARGETS,
  CONTACT_INPUTS,
  DATA_CONNECTOR_LICENSE,
  callDataConnector,
  inputLabel,
  mappingSourceLabel,
  parseAiChoices,
  reasonLabel,
  recipeInputs,
  useConnectorIdentity,
  useDataConnectors,
} from './dataConnectors';
import catalog from '../config/dataConnectorCatalog.json';
import { BatchReport, QualityReport } from './ConnectorTestReport';
import { RIGHTS_POINTS, RIGHTS_TITLE, RIGHTS_VERSION, requesterUserAgent } from '../shared/connectorRights';
import './DataConnectorsSettings.css';

const DATA_SOURCE_TEMPLATES = catalog.templates;
const DATA_SOURCE_CATEGORIES = catalog.categories;

const UI_LANGUAGE = typeof navigator !== 'undefined' && /^de/i.test(navigator.language || '') ? 'German' : 'English';
const PLACEHOLDER_ONLY_RE = /^\{\{\s*([a-z0-9_]+)\s*\}\}$/i;

const emptyDraft = () => ({
  name: '',
  goal: '',
  docsUrl: '',
  docsText: '',
  baseUrl: '',
  method: 'GET',
  path: '',
  query: [],
  body: [],
  headers: [],
  auth: { type: 'header', name: '' },
  response: { itemsPath: '', maxItems: 5, fields: [] },
  mappings: [],
  useInEmails: true,
  icon: '',
  paceSec: 0,
  templateId: '',
});

const hydrateParams = (list) => (list || []).map((p) => ({
  name: p.name || '',
  value: p.value || '',
  ...(p.optional ? { optional: true } : {}),
  ...(p.json ? { json: true } : {}),
  ...(p.ai ? { ai: { ask: p.ai.ask || '', choicesText: (p.ai.choices || []).join('\n') } } : {}),
}));

const serializeParams = (list) => (list || []).map((p) => {
  const out = { name: p.name, value: p.ai ? '' : (p.value || '') };
  if (p.optional) out.optional = true;
  if (p.json) out.json = true;
  if (p.ai) out.ai = { ask: String(p.ai.ask || '').trim(), choices: parseAiChoices(p.ai.choicesText) };
  return out;
});

const draftFromConnector = (c) => ({
  ...emptyDraft(),
  name: c.name || '',
  goal: c.goal || '',
  icon: c.icon || '',
  paceSec: c.paceSec || 0,
  templateId: c.templateId || '',
  baseUrl: c.baseUrl || '',
  method: c.method || 'GET',
  path: c.path || '',
  query: hydrateParams(c.query),
  body: hydrateParams(c.body),
  headers: hydrateParams(c.headers),
  auth: { type: c.auth?.type || 'header', name: c.auth?.name || '' },
  response: {
    itemsPath: c.response?.itemsPath || '',
    totalPath: c.response?.totalPath || '',
    maxItems: c.response?.maxItems || 5,
    fields: c.response?.fields || [],
  },
  mappings: (c.mappings || []).map((m) => ({ source: m.source, target: m.target, newFieldLabel: '' })),
  useInEmails: c.useInEmails !== false,
});

const recipeOf = (draft) => ({
  name: draft.name,
  goal: draft.goal,
  baseUrl: draft.baseUrl,
  method: draft.method,
  path: draft.path,
  query: serializeParams(draft.query),
  body: draft.method === 'POST' ? serializeParams(draft.body) : [],
  headers: serializeParams(draft.headers),
  auth: draft.auth,
  response: draft.response,
  mappings: draft.mappings.filter((m) => m.target).map((m) => ({ source: m.source, target: m.target })),
  useInEmails: draft.useInEmails,
  icon: draft.icon,
  paceSec: draft.paceSec,
  templateId: draft.templateId,
});

const hasInput = (contact, key) => {
  if (String(key).startsWith('ai:')) return true;
  return key === 'lead_domain' ? contact.lead_website : key === 'lead_address' ? contact.lead_city : contact[key];
};

const missingSentence = (keys, customFields) => {
  const ai = keys.filter((k) => String(k).startsWith('ai:'));
  const fields = keys.filter((k) => !String(k).startsWith('ai:'));
  const parts = [];
  if (fields.length) parts.push(`This contact has no ${fields.map((k) => inputLabel(k, customFields)).join(', ')}.`);
  if (ai.length) parts.push(`AI could not choose ${ai.map((k) => k.slice(3)).join(', ')} from the allowed list. Mark it optional to skip the parameter, or pick another contact.`);
  return parts.join(' ');
};

/** Turns the assistant's setup warning into something a person can act on. */
function assistGap(error, baseUrl) {
  if (!error) return null;
  if (/API address|https:\/\//i.test(error)) {
    let ok = false;
    try {
      const url = new URL(baseUrl);
      ok = url.protocol === 'https:' && !/^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/i.test(url.hostname);
    } catch (_) {
      ok = false;
    }
    if (ok) return null;
    return {
      title: 'The platform address is missing',
      text: 'Paste the address of the platform. It starts with https:// and is a public website, not something on your own computer.',
      address: true,
    };
  }
  return { title: 'One thing to fix before this can run', text: error, address: false };
}

const hostOf = (url) => {
  try {
    return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).host;
  } catch (_) {
    return '';
  }
};

const timeAgo = (ms) => {
  if (!ms) return 'never';
  const min = Math.round((Date.now() - ms) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
};

// ── Parameter rows: "send <contact field> as <name>" in plain words ────────

function ParamRows({ title, rows, onChange, customFields, allowOptional = true }) {
  const inputOptions = [
    ...Object.entries(CONTACT_INPUTS).map(([key, label]) => ({ key, label })),
    ...customFields.map((f) => ({ key: f.key, label: f.label })),
  ];
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <div className="dcx-params">
      <div className="dcx-params-head">
        <span>{title}</span>
        <button type="button" className="dcx-link" onClick={() => onChange([...rows, { name: '', value: '' }])}>
          <i className="fa fa-plus" /> Add
        </button>
      </div>
      {!rows.length && <div className="dcx-muted">Nothing sent here.</div>}
      {rows.map((row, i) => {
        const placeholder = !row.ai && String(row.value || '').match(PLACEHOLDER_ONLY_RE);
        const mode = row.ai ? '__ai' : placeholder ? placeholder[1].toLowerCase() : '__fixed';
        const setSource = (value) => {
          if (value === '__ai') update(i, { value: '', ai: row.ai || { ask: '', choicesText: '' } });
          else if (value === '__fixed') update(i, { value: '', ai: null });
          else update(i, { value: `{{${value}}}`, ai: null });
        };
        return (
          <div className="dcx-param-block" key={i}>
          <div className="dcx-param">
            <input
              className="dcx-input dcx-input--name"
              value={row.name}
              placeholder="name"
              onChange={(e) => update(i, { name: e.target.value })}
            />
            <span className="dcx-param-eq">=</span>
            <select
              className="dcx-input"
              value={mode}
              aria-label={`Source for ${row.name || 'parameter'}`}
              onChange={(e) => setSource(e.target.value)}
            >
              <optgroup label="From the contact">
                {inputOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </optgroup>
              <option value="__fixed">Fixed value…</option>
              <option value="__ai">Let AI find…</option>
            </select>
            {mode === '__fixed' && (
              <input
                className="dcx-input"
                value={row.value}
                placeholder="value"
                onChange={(e) => update(i, { value: e.target.value })}
              />
            )}
            {row.json && <span className="dcx-tag-json" title="Sent as JSON (list, number or object), not as text">JSON</span>}
            {allowOptional && (
              <label className="dcx-check" title={mode === '__ai' ? 'Leave this parameter out when none of the allowed values fits' : 'Skip this parameter when the contact has no value'}>
                <input type="checkbox" checked={!!row.optional} onChange={(e) => update(i, { optional: e.target.checked })} />
                optional
              </label>
            )}
            <button type="button" className="dcx-icon-btn" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} aria-label="Remove">
              <i className="fa fa-xmark" />
            </button>
          </div>
          {mode === '__ai' && (
            <div className="dcx-ai">
              <textarea
                className="dcx-input dcx-textarea"
                rows={4}
                value={row.ai?.choicesText || ''}
                placeholder={'One allowed value per line\nBakery\nElectrician\nPainter'}
                aria-label={`Allowed values for ${row.name || 'parameter'}`}
                onChange={(e) => update(i, { ai: { ...row.ai, choicesText: e.target.value } })}
              />
              <input
                className="dcx-input"
                value={row.ai?.ask || ''}
                placeholder="Optional hint, e.g. the industry this company belongs to"
                aria-label={`Hint for ${row.name || 'parameter'}`}
                onChange={(e) => update(i, { ai: { ...row.ai, ask: e.target.value } })}
              />
              <p className="dcx-muted dcx-small">
                A small model reads the company name, industry, description, website, address and custom columns here, then sends only one value from this list.
              </p>
            </div>
          )}
          </div>
        );
      })}
    </div>
  );
}

function AssistBrief({ assist, baseUrl, onBaseUrl }) {
  const gap = assistGap(assist.validationError, baseUrl);
  return (
    <div className={`dcx-explain dcx-explain--${assist.confidence}`}>
      <div className="dcx-explain-top">
        <i className="fa fa-wand-magic-sparkles" />
        <p className="dcx-explain-lead">{assist.explanation || 'Here is a first setup from the documentation. Check it, then try it on one contact.'}</p>
      </div>
      {assist.confidence === 'low' && (
        <div className="dcx-hint"><i className="fa fa-triangle-exclamation" /> The documentation was thin, so treat this as a draft. Check the fields below and run a test on one contact.</div>
      )}
      {assist.needs?.length > 0 && (
        <div className="dcx-explain-block">
          <div className="dcx-explain-label"><i className="fa fa-clipboard-check" /> You'll need</div>
          <ul className="dcx-explain-list">
            {assist.needs.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>
      )}
      {assist.questions?.length > 0 && (
        <div className="dcx-explain-block">
          <div className="dcx-explain-label"><i className="fa fa-circle-question" /> Worth deciding</div>
          <ul className="dcx-explain-list dcx-explain-list--q">
            {assist.questions.map((q) => <li key={q}>{q}</li>)}
          </ul>
        </div>
      )}
      {gap && (
        <div className="dcx-explain-fix">
          <div className="dcx-explain-label"><i className="fa fa-location-dot" /> {gap.title}</div>
          <p>{gap.text}</p>
          {gap.address && (
            <input
              className="dcx-input"
              value={baseUrl}
              placeholder="https://api.platform.com"
              aria-label="Platform address"
              onChange={(e) => onBaseUrl(e.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ── Wizard ──────────────────────────────────────────────────────────────────

function ConnectorWizard({ userId, connector, template, senderAgent, onClose }) {
  const { customFields, addField, MAX_TOTAL_FIELDS } = useCustomFields(userId);
  const [step, setStep] = useState(() => {
    if (template) return 'test';
    return connector ? 'recipe' : 'goal';
  });
  const [draft, setDraft] = useState(() => {
    if (template) return draftFromConnector({ ...template.recipe, templateId: template.id });
    return connector ? draftFromConnector(connector) : emptyDraft();
  });
  const [apiKey, setApiKey] = useState('');
  const [assist, setAssist] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [showDocsText, setShowDocsText] = useState(false);
  const [showTech, setShowTech] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactId, setContactId] = useState('');
  const [test, setTest] = useState(null);
  const [batch, setBatch] = useState(null);
  const [rightsOk, setRightsOk] = useState(false);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setResponse = (patch) => setDraft((d) => ({ ...d, response: { ...d.response, ...patch } }));
  const inputs = useMemo(() => recipeInputs(draft), [draft]);
  const keyStored = !!connector?.hasKey;

  // Same rule as the server: confirm again for another host, more contact data or a new text.
  const targetHost = hostOf(draft.baseUrl).toLowerCase();
  const confirmedInputs = new Set(connector?.rights?.inputs || []);
  const rightsNeeded = !connector?.rights
    || connector.rights.version !== RIGHTS_VERSION
    || connector.rights.host !== targetHost
    || inputs.some((k) => !confirmedInputs.has(k));
  const rightsScope = `${targetHost}|${inputs.join(',')}`;
  useEffect(() => { setRightsOk(false); }, [rightsScope]);

  useEffect(() => {
    if (step !== 'test' || contacts.length || !userId) return;
    getDocs(query(collection(firestore, `World/${userId}/db`), limit(60)))
      .then((snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((c) => c.business_name)
          .sort((a, b) => String(a.business_name).localeCompare(String(b.business_name)));
        setContacts(list);
        const firstComplete = list.find((c) => inputs.every((k) => hasInput(c, k)));
        setContactId((firstComplete || list[0])?.id || '');
      })
      .catch(() => setContacts([]));
  }, [step, contacts.length, userId, inputs]);

  // Ready-made sources: try them on a matching contact right away (no key needed).
  const autoTestRef = useRef(false);
  const contact = contacts.find((c) => c.id === contactId);
  const templateNeedsKey = !!template && draft.auth.type !== 'none' && !apiKey.trim();
  useEffect(() => {
    if (!template || autoTestRef.current || !contact || templateNeedsKey) return;
    if (!inputs.every((k) => hasInput(contact, k))) return;
    autoTestRef.current = true;
    runTest({ suggest: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, contact, templateNeedsKey, inputs]);

  const runAssist = async () => {
    setError('');
    if (!draft.goal.trim()) return setError('Describe what you want to get for each contact.');
    if (!draft.docsUrl.trim() && !draft.docsText.trim() && !draft.baseUrl.trim()) {
      return setError('Add a link to the API documentation, paste it, or give the API address.');
    }
    setBusy('assist');
    try {
      const res = await callDataConnector(userId, 'assist', {
        name: draft.name,
        goal: draft.goal,
        docsUrl: draft.docsUrl,
        docsText: draft.docsText,
        baseUrl: draft.baseUrl,
        language: UI_LANGUAGE,
      });
      setAssist(res);
      setDraft((d) => ({ ...d, ...res.draft, name: d.name || res.draft.name, docsUrl: d.docsUrl, docsText: d.docsText }));
      setStep('recipe');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const runTest = async ({ suggest }) => {
    setError('');
    if (!contactId) return setError('Pick a contact to test with.');
    setBusy(suggest ? 'suggest' : 'test');
    try {
      const res = await callDataConnector(userId, 'test', {
        connectorId: connector?.id || null,
        config: recipeOf(draft),
        apiKey,
        contactId,
        candidateIds: contacts.map((c) => c.id).filter((id) => id !== contactId).slice(0, 30),
        suggest,
        language: UI_LANGUAGE,
      });
      if (res.usedContactId) setContactId(res.usedContactId);
      setTest(res);
      if (res.suggestion) {
        const s = res.suggestion;
        setDraft((d) => ({
          ...d,
          response: { itemsPath: s.itemsPath, maxItems: s.maxItems, fields: s.fields },
          mappings: s.mappings.map((m) => ({ source: m.source, target: m.target || '', newFieldLabel: m.newFieldLabel || '' })),
          useInEmails: s.useInEmails,
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  // Up to 5 contacts that have what the source needs, the selected one first.
  const batchContacts = () => {
    const ready = contacts.filter((c) => inputs.every((k) => hasInput(c, k)));
    const pool = ready.length ? ready : contacts;
    const ordered = [...pool.filter((c) => c.id === contactId), ...pool.filter((c) => c.id !== contactId)];
    return ordered.slice(0, 5).map((c) => c.id);
  };

  const runBatch = async () => {
    setError('');
    const contactIds = batchContacts();
    if (!contactIds.length) return setError('There are no contacts to test with yet.');
    setBusy('batch');
    try {
      const res = await callDataConnector(userId, 'testBatch', {
        connectorId: connector?.id || null,
        config: recipeOf(draft),
        apiKey,
        contactIds,
        language: UI_LANGUAGE,
      });
      setBatch(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const keepPath = (path, label) => {
    if (draft.response.fields.some((f) => f.path === path) || draft.response.fields.length >= 20) return;
    setResponse({ fields: [...draft.response.fields, { path, label: label.slice(0, 40) }] });
  };

  const save = async () => {
    setError('');
    setBusy('save');
    try {
      const mappings = [];
      for (const m of draft.mappings) {
        if (m.target) {
          mappings.push(m);
        } else if (m.newFieldLabel.trim()) {
          const created = await addField(m.newFieldLabel.trim(), false, m.source === 'count' ? 'number' : 'text');
          if (!created.success) throw new Error(`Field "${m.newFieldLabel}": ${created.error}`);
          mappings.push({ ...m, target: created.key, newFieldLabel: '' });
        }
      }
      const next = { ...draft, mappings };
      setDraft(next);
      await callDataConnector(userId, 'save', {
        connectorId: connector?.id || null,
        config: recipeOf(next),
        apiKey,
        rightsConfirmed: rightsNeeded && rightsOk ? RIGHTS_VERSION : null,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  // Lists the test found + "the whole answer is one result".
  const listOptions = useMemo(() => {
    if (!test?.shape) return [];
    const opts = (test.listPaths || []).map((l) => ({
      path: l.path,
      label: `${l.path || 'Answer'} — list with ${l.length} entries`,
      fields: l.fields,
    }));
    if (!opts.some((o) => o.path === '')) {
      opts.push({
        path: '',
        label: 'Whole answer (one result)',
        fields: test.shape.paths.filter((p) => p.type !== 'list' && !p.path.includes('[')).map((p) => p.path),
      });
    }
    const current = draft.response.itemsPath;
    if (current && !opts.some((o) => o.path === current)) {
      opts.unshift({ path: current, label: `${current} — as set up`, fields: [] });
    }
    return opts;
  }, [test, draft.response.itemsPath]);
  const currentList = listOptions.find((o) => o.path === draft.response.itemsPath) || listOptions[0];
  const keptPaths = new Set(draft.response.fields.map((f) => f.path));
  const listFields = currentList ? currentList.fields.slice(0, 40) : [];
  // Kept fields stay visible even when they sit deeper than the sampled paths.
  const fieldChoices = [...draft.response.fields.map((f) => f.path).filter((p) => !listFields.includes(p)), ...listFields];

  const toggleField = (path) => {
    const fields = keptPaths.has(path)
      ? draft.response.fields.filter((f) => f.path !== path)
      : [...draft.response.fields, { path, label: path.split('.').pop().replace(/_/g, ' ') }];
    setResponse({ fields: fields.slice(0, 20) });
  };

  const sourceOptions = [
    { value: 'count', label: 'Number of results' },
    { value: 'summary', label: 'All results as text' },
    ...draft.response.fields.flatMap((f) => [
      { value: `first:${f.path}`, label: `${f.label} (first result)` },
      { value: `list:${f.path}`, label: `${f.label} (all results)` },
    ]),
  ];
  const updateMapping = (i, patch) => set({ mappings: draft.mappings.map((m, idx) => (idx === i ? { ...m, ...patch } : m)) });
  const newFieldsPlanned = draft.mappings.filter((m) => !m.target && m.newFieldLabel.trim()).length;
  const fieldRoom = MAX_TOTAL_FIELDS - customFields.length;

  const steps = [
    { id: 'goal', label: 'What you want' },
    { id: 'recipe', label: 'How we call it' },
    { id: 'test', label: 'Test & keep' },
  ];

  return (
    <div className="dcx-wizard">
      <div className="dcx-wizard-head">
        <button type="button" className="dcx-link" onClick={onClose}><i className="fa fa-arrow-left" /> All data sources</button>
        <ol className="dcx-steps">
          {steps.map((s, i) => (
            <li key={s.id} className={step === s.id ? 'active' : ''}>
              <button type="button" onClick={() => (s.id !== 'test' || draft.baseUrl) && setStep(s.id)}>
                <span>{i + 1}</span>{s.label}
              </button>
            </li>
          ))}
        </ol>
      </div>

      {error && <div className="dcx-error"><i className="fa fa-circle-exclamation" /> {error}</div>}

      {step === 'goal' && (
        <section className="dcx-panel">
          <h3>Which platform, and what should it find for each contact?</h3>
          <p className="dcx-lead">
            Describe it like you would to a colleague. Our assistant reads the platform's API documentation,
            works out what it needs and how to ask, and you test it on a real contact before anything runs.
          </p>
          <label className="dcx-label">Platform name</label>
          <input className="dcx-input" value={draft.name} placeholder="e.g. Bauradar" onChange={(e) => set({ name: e.target.value })} />

          <label className="dcx-label">What should it find for each contact?</label>
          <textarea
            className="dcx-input dcx-textarea"
            rows={3}
            value={draft.goal}
            placeholder="e.g. For every contact, find up to 5 current construction projects in their city with project name, phase and budget."
            onChange={(e) => set({ goal: e.target.value })}
          />

          <label className="dcx-label">Link to the API documentation</label>
          <input className="dcx-input" value={draft.docsUrl} placeholder="https://… (docs page, OpenAPI or Swagger file)" onChange={(e) => set({ docsUrl: e.target.value })} />
          <button type="button" className="dcx-link" onClick={() => setShowDocsText((v) => !v)}>
            <i className={`fa fa-chevron-${showDocsText ? 'up' : 'down'}`} /> No public docs? Paste them or an example request
          </button>
          {showDocsText && (
            <textarea
              className="dcx-input dcx-textarea dcx-mono"
              rows={6}
              value={draft.docsText}
              placeholder="Paste the relevant part of the docs, an example curl command or an example answer."
              onChange={(e) => set({ docsText: e.target.value })}
            />
          )}

          <label className="dcx-label">API address <span className="dcx-muted">(optional — the assistant usually finds it)</span></label>
          <input className="dcx-input" value={draft.baseUrl} placeholder="https://api.platform.com/v1" onChange={(e) => set({ baseUrl: e.target.value })} />

          <div className="dcx-actions">
            <button type="button" className="altBtn altBtn--petrol" onClick={runAssist} disabled={busy === 'assist'}>
              <i className={`fa ${busy === 'assist' ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`} />
              {busy === 'assist' ? 'Reading the documentation…' : 'Let the assistant set it up'}
            </button>
            <button type="button" className="dcx-link" onClick={() => setStep('recipe')}>Set up manually</button>
          </div>
        </section>
      )}

      {step === 'recipe' && (
        <section className="dcx-panel">
          <h3>How we ask {draft.name || 'the platform'}</h3>
          {assist && (
            <AssistBrief
              assist={assist}
              baseUrl={draft.baseUrl}
              onBaseUrl={(baseUrl) => set({ baseUrl })}
            />
          )}

          <div className="dcx-sends">
            <span className="dcx-label">For each contact we send</span>
            {inputs.length
              ? <div className="dcx-chips">{inputs.map((k) => <span key={k} className="dcx-chip"><i className="fa fa-user" /> {inputLabel(k, customFields)}</span>)}</div>
              : <span className="dcx-muted">No contact data — the request is the same for every contact.</span>}
          </div>

          <ParamRows title="Parameters" rows={draft.query} onChange={(query) => set({ query })} customFields={customFields} />

          <div className="dcx-key">
            <span className="dcx-label">API key</span>
            <div className="dcx-key-row">
              <select
                className="dcx-input"
                value={draft.auth.type}
                onChange={(e) => set({ auth: { ...draft.auth, type: e.target.value } })}
              >
                {AUTH_TYPES.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
              {(draft.auth.type === 'header' || draft.auth.type === 'query') && (
                <input
                  className="dcx-input dcx-input--name"
                  value={draft.auth.name || ''}
                  placeholder={AUTH_TYPES.find((a) => a.id === draft.auth.type)?.hint}
                  onChange={(e) => set({ auth: { ...draft.auth, name: e.target.value } })}
                />
              )}
              {draft.auth.type !== 'none' && (
                <input
                  className="dcx-input"
                  type="password"
                  autoComplete="new-password"
                  value={apiKey}
                  placeholder={keyStored ? `Stored (${connector.keyPreview}) — type to replace` : 'Paste your API key'}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              )}
            </div>
            <p className="dcx-muted dcx-small">
              <i className="fa fa-lock" /> Stored encrypted on our server, never shown again, and only ever sent to {hostOf(draft.baseUrl) || 'this platform'}.
              Changing the API address requires entering the key again.
            </p>
          </div>

          <button type="button" className="dcx-link" onClick={() => setShowTech((v) => !v)}>
            <i className={`fa fa-chevron-${showTech ? 'up' : 'down'}`} /> Technical details
          </button>
          {showTech && (
            <div className="dcx-tech">
              <label className="dcx-label">API address</label>
              <input className="dcx-input dcx-mono" value={draft.baseUrl} onChange={(e) => set({ baseUrl: e.target.value })} />
              <div className="dcx-row">
                <div>
                  <label className="dcx-label">Method</label>
                  <select className="dcx-input" value={draft.method} onChange={(e) => set({ method: e.target.value })}>
                    <option>GET</option>
                    <option>POST</option>
                  </select>
                </div>
                <div className="dcx-grow">
                  <label className="dcx-label">Endpoint path</label>
                  <input className="dcx-input dcx-mono" value={draft.path} placeholder="/projects/search" onChange={(e) => set({ path: e.target.value })} />
                </div>
              </div>
              {draft.method === 'POST' && (
                <ParamRows title="JSON body" rows={draft.body} onChange={(body) => set({ body })} customFields={customFields} />
              )}
              <ParamRows title="Extra headers" rows={draft.headers} onChange={(headers) => set({ headers })} customFields={customFields} allowOptional={false} />
            </div>
          )}

          <div className="dcx-actions">
            <button type="button" className="altBtn altBtn--petrol" onClick={() => setStep('test')} disabled={!draft.baseUrl}>
              <i className="fa fa-vial" /> Test with a contact
            </button>
          </div>
        </section>
      )}

      {step === 'test' && (
        <section className="dcx-panel">
          {template && (
            <div className="dcx-tpl">
              <div className="dcx-tpl-icon"><i className={`fa ${template.icon}`} /></div>
              <div className="dcx-tpl-body">
                <div className="dcx-tpl-title">{template.name} <span className="dcx-muted">· {template.provider}</span></div>
                <p>{template.description}</p>
                <div className="dcx-chips">
                  {inputs.map((k) => <span key={k} className="dcx-chip"><i className="fa fa-user" /> {inputLabel(k, customFields)}</span>)}
                  <span className="dcx-chip"><i className="fa fa-earth-europe" /> {template.coverage}</span>
                </div>
                {template.tip && <div className="dcx-hint"><i className="fa fa-lightbulb" /> {template.tip}</div>}
                {draft.auth.type !== 'none' && (
                  <div className="dcx-tpl-key">
                    <input
                      className="dcx-input"
                      type="password"
                      autoComplete="new-password"
                      value={apiKey}
                      placeholder="Paste your free API key"
                      onChange={(e) => setApiKey(e.target.value)}
                    />
                    {template.keyUrl && (
                      <a className="dcx-link" href={template.keyUrl} target="_blank" rel="noopener noreferrer">
                        <i className="fa fa-arrow-up-right-from-square" /> Get a free key
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <h3>{template ? 'See it work on one of your contacts' : 'Test on a real contact'}</h3>
          {template && !test && !busy && contact && !inputs.every((k) => hasInput(contact, k)) && (
            <p className="dcx-muted">This contact has no {inputs.filter((k) => !hasInput(contact, k)).map((k) => inputLabel(k, customFields)).join(' or ')}. Pick one that has it, or add the source anyway.</p>
          )}
          <div className="dcx-row">
            <select className="dcx-input dcx-grow" value={contactId} onChange={(e) => setContactId(e.target.value)}>
              {!contacts.length && <option value="">Loading contacts…</option>}
              {contacts.map((c) => {
                const missing = inputs.filter((k) => !hasInput(c, k));
                return (
                  <option key={c.id} value={c.id}>
                    {c.business_name}{c.lead_city ? ` · ${c.lead_city}` : ''}{missing.length ? ` (missing ${missing.map((k) => inputLabel(k, customFields)).join(', ')})` : ''}
                  </option>
                );
              })}
            </select>
            <button type="button" className="altBtn altBtn--petrol" onClick={() => runTest({ suggest: !template && !test?.ok })} disabled={!!busy}>
              <i className={`fa ${busy === 'test' || busy === 'suggest' ? 'fa-spinner fa-spin' : 'fa-play'}`} />
              {busy === 'suggest' ? 'Calling & analysing…' : busy === 'test' ? 'Calling…' : test ? 'Run again' : 'Run test'}
            </button>
            {test?.ok && (
              <button type="button" className="altBtn altBtn--petrol" onClick={runBatch} disabled={!!busy} title="Runs the source on 5 contacts without saving anything">
                <i className={`fa ${busy === 'batch' ? 'fa-spinner fa-spin' : 'fa-users'}`} />
                {busy === 'batch' ? 'Testing 5 contacts…' : 'Test on 5 contacts'}
              </button>
            )}
          </div>

          {test && (
            <div className={`dcx-result ${test.ok ? 'ok' : 'fail'}`}>
              {test.request && <code className="dcx-request">{test.request}</code>}
              <div className="dcx-result-line">
                <i className={`fa ${test.ok ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                {test.ok
                  ? (test.reason === 'no_match' ? 'Connected — the platform found nothing for this contact.' : `Connected — ${test.preview?.total ?? 0} result(s).`)
                  : `${reasonLabel(test.reason)}${test.status ? ` (HTTP ${test.status})` : ''}`}
              </div>
              {test.diagnosis ? (
                <div className="dcx-diagnosis">
                  <p>{test.diagnosis.summary}</p>
                  {test.diagnosis.action && <p className="dcx-diagnosis-action">{test.diagnosis.action}</p>}
                </div>
              ) : (
                test.reasonText && !test.ok && <p className="dcx-muted">{test.reasonText}</p>
              )}
              {test.aiPicks?.length > 0 && (
                <p className="dcx-muted">
                  AI chose {test.aiPicks.map((p) => (p.value ? `${p.name}: ${p.value}` : `${p.name}: nothing that fits`)).join(' · ')}
                </p>
              )}
              {test.missing?.length > 0 && <p className="dcx-muted">{missingSentence(test.missing, customFields)}</p>}
              {test.detail && (
                test.diagnosis
                  ? <details className="dcx-detail-fold"><summary>What the platform answered</summary><pre className="dcx-detail">{test.detail}</pre></details>
                  : <pre className="dcx-detail">{test.detail}</pre>
              )}
            </div>
          )}

          {test?.ok && test.quality && (
            <QualityReport
              quality={test.quality}
              keptPaths={keptPaths}
              onKeepPath={keepPath}
              belongsWhy={test.quality.review?.belongs?.find((b) => b.verdict === 'no')?.why}
            />
          )}

          <BatchReport
            batch={batch}
            inputs={inputs}
            keptPaths={keptPaths}
            onKeepPath={keepPath}
            inputLabelFor={(k) => inputLabel(k, customFields)}
          />

          {test?.ok && test.shape && (
            <>
              {test.suggestion?.explanation && (
                <div className="dcx-explain"><div className="dcx-explain-top"><i className="fa fa-wand-magic-sparkles" /><span>{test.suggestion.explanation}</span></div></div>
              )}

              <label className="dcx-label">Where are the results?</label>
              <div className="dcx-row">
                <select className="dcx-input dcx-grow" value={draft.response.itemsPath} onChange={(e) => setResponse({ itemsPath: e.target.value, totalPath: '', fields: [] })}>
                  {listOptions.map((o) => <option key={o.path || 'root'} value={o.path}>{o.label}</option>)}
                </select>
                <label className="dcx-inline">
                  keep up to
                  <input
                    type="number"
                    min={1}
                    max={25}
                    className="dcx-input dcx-input--num"
                    value={draft.response.maxItems}
                    onChange={(e) => setResponse({ maxItems: Math.min(25, Math.max(1, Number(e.target.value) || 1)) })}
                  />
                </label>
              </div>

              <label className="dcx-label">What to keep from each result</label>
              <div className="dcx-fields">
                {fieldChoices.map((path) => {
                  const kept = draft.response.fields.find((f) => f.path === path);
                  return (
                    <div key={path} className={`dcx-field ${kept ? 'on' : ''}`}>
                      <label>
                        <input type="checkbox" checked={!!kept} onChange={() => toggleField(path)} />
                        <code>{path}</code>
                      </label>
                      {kept && (
                        <input
                          className="dcx-input dcx-input--label"
                          value={kept.label}
                          onChange={(e) => setResponse({ fields: draft.response.fields.map((f) => (f.path === path ? { ...f, label: e.target.value } : f)) })}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {(test.suggestedPreview || test.preview)?.items?.length > 0 && (
                <div className="dcx-preview">
                  <span className="dcx-label">Preview</span>
                  {(test.suggestedPreview || test.preview).items.map((values, i) => (
                    <div key={i} className="dcx-preview-item">
                      <span className="dcx-preview-n">{i + 1}</span>
                      {values.map((v) => <span key={v.label}><b>{v.label}:</b> {v.value}</span>)}
                    </div>
                  ))}
                  <button type="button" className="dcx-link" onClick={() => runTest({ suggest: false })}>
                    <i className="fa fa-rotate" /> Refresh preview with this selection
                  </button>
                </div>
              )}

              <div className="dcx-params">
                <div className="dcx-params-head">
                  <span>Save into a column <span className="dcx-muted">(optional)</span></span>
                  <button type="button" className="dcx-link" onClick={() => set({ mappings: [...draft.mappings, { source: 'summary', target: '', newFieldLabel: '' }] })}>
                    <i className="fa fa-plus" /> Add column
                  </button>
                </div>
                <p className="dcx-muted dcx-small">The full result always stays in Intelligence. Description keeps the text already on the contact and adds this underneath. A column of your own also shows there, and in the table.</p>
                {draft.mappings.map((m, i) => (
                  <div className="dcx-param" key={i}>
                    <select className="dcx-input" value={m.source} onChange={(e) => updateMapping(i, { source: e.target.value })}>
                      {sourceOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      {!sourceOptions.some((o) => o.value === m.source) && <option value={m.source}>{mappingSourceLabel(m.source, draft.response.fields)}</option>}
                    </select>
                    <span className="dcx-param-eq"><i className="fa fa-arrow-right" /></span>
                    <select
                      className="dcx-input"
                      value={m.target || '__new'}
                      onChange={(e) => updateMapping(i, { target: e.target.value === '__new' ? '' : e.target.value })}
                    >
                      <optgroup label="On the contact">
                        {COLUMN_TARGETS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </optgroup>
                      {customFields.length > 0 && (
                        <optgroup label="Your columns">
                          {customFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </optgroup>
                      )}
                      <option value="__new">New column…</option>
                    </select>
                    {!m.target && (
                      <input className="dcx-input" value={m.newFieldLabel} placeholder="Column name" onChange={(e) => updateMapping(i, { newFieldLabel: e.target.value })} />
                    )}
                    <button type="button" className="dcx-icon-btn" onClick={() => set({ mappings: draft.mappings.filter((_, idx) => idx !== i) })} aria-label="Remove">
                      <i className="fa fa-xmark" />
                    </button>
                  </div>
                ))}
                {newFieldsPlanned > fieldRoom && (
                  <div className="dcx-hint"><i className="fa fa-triangle-exclamation" /> Only {fieldRoom} custom field slot(s) left — reuse an existing column or remove one.</div>
                )}
              </div>

              <label className="dcx-toggle">
                <input type="checkbox" checked={draft.useInEmails} onChange={(e) => set({ useInEmails: e.target.checked })} />
                <span>Use these results when writing emails (manual, bulk and agents)</span>
              </label>
            </>
          )}

          {rightsNeeded && (
            <div className="dcx-rights" aria-label="Confirm your rights">
              <div className="dcx-rights-sends">
                <i className="fa fa-shield-halved" />
                <span>
                  For each contact we send <b>{inputs.length ? inputs.map((k) => inputLabel(k, customFields)).join(', ') : 'no contact data'}</b> to <b>{targetHost || 'this platform'}</b>.
                </span>
              </div>
              {senderAgent && (
                <div className="dcx-rights-sends">
                  <i className="fa fa-id-badge" />
                  <span>Signed as <code>{senderAgent}</code></span>
                </div>
              )}
              <span className="dcx-rights-title">{RIGHTS_TITLE}</span>
              <ol>{RIGHTS_POINTS.map((p) => <li key={p}>{p}</li>)}</ol>
              <label className="dcx-rights-check">
                <input type="checkbox" checked={rightsOk} onChange={(e) => setRightsOk(e.target.checked)} />
                <span>I confirm the points above.</span>
              </label>
              <p className="dcx-muted dcx-small">We record your confirmation with date, user and this exact text.</p>
            </div>
          )}

          <div className="dcx-actions">
            <button
              type="button"
              className="altBtn altBtn--save"
              onClick={save}
              disabled={busy === 'save' || newFieldsPlanned > fieldRoom || (!test?.ok && !connector && !template) || templateNeedsKey || (rightsNeeded && !rightsOk)}
              title={!test?.ok && !connector && !template ? 'Run a successful test first' : undefined}
            >
              <i className={`fa ${busy === 'save' ? 'fa-spinner fa-spin' : 'fa-check'}`} />
              {connector ? 'Save changes' : template ? 'Add to my researchers' : 'Save data source'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Sender identity: requests name the customer, not the platform ──────────

function SenderIdentity({ identityState }) {
  const { own, account, identity, save } = identityState;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ company: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const open = () => {
    setForm({ company: own.company || account.company || '', email: own.email || account.email || '' });
    setError('');
    setEditing(true);
  };
  const submit = async () => {
    if (form.email.trim() && !/^[^\s@<>()"]+@[^\s@<>()"]+\.[a-z]{2,}$/i.test(form.email.trim())) {
      return setError('Enter a valid contact email.');
    }
    setSaving(true);
    try {
      await save(form);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dcx-sender" aria-label="Sender identity">
      <div className="dcx-sender-row">
        <i className="fa fa-id-badge" />
        <div className="dcx-grow">
          <b>Requests are signed with your company</b>
          <p className="dcx-muted dcx-small">
            Platforms see your company and contact email as the sender — so limits and blocks apply to your account only, as many free APIs require.
          </p>
          {!editing && <code className="dcx-sender-agent">{requesterUserAgent(identity)}</code>}
        </div>
        {!editing && <button type="button" className="dcx-link" onClick={open}><i className="fa fa-pen" /> Change</button>}
      </div>
      {editing && (
        <div className="dcx-sender-form">
          <input className="dcx-input" value={form.company} placeholder="Company name" onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <input className="dcx-input" value={form.email} placeholder="Contact email for platforms" onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <code className="dcx-sender-agent">{requesterUserAgent(form)}</code>
          {error && <div className="dcx-hint"><i className="fa fa-circle-exclamation" /> {error}</div>}
          <div className="dcx-actions">
            <button type="button" className="altBtn altBtn--save" onClick={submit} disabled={saving}>
              <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`} /> Save sender
            </button>
            <button type="button" className="dcx-link" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Store: ready-made free sources ──────────────────────────────────────────

function SourceStore({ connectors, onAdd, onOpen }) {
  const [category, setCategory] = useState('all');
  const added = useMemo(() => {
    const byTemplate = {};
    connectors.forEach((c) => { if (c.templateId && !byTemplate[c.templateId]) byTemplate[c.templateId] = c; });
    return byTemplate;
  }, [connectors]);
  const shown = DATA_SOURCE_TEMPLATES.filter((t) => category === 'all' || t.category === category);

  return (
    <section className="dcx-store">
      <div className="dcx-store-head">
        <div>
          <h3><i className="fa fa-store" /> Data source store</h3>
          <p>Free, ready-made researchers. Add one with a click — we test it on one of your contacts right away, and you can fine-tune it afterwards.</p>
        </div>
        <div className="dcx-store-filter" role="tablist">
          <button type="button" className={category === 'all' ? 'on' : ''} onClick={() => setCategory('all')}>All</button>
          {DATA_SOURCE_CATEGORIES.map((c) => (
            <button key={c.id} type="button" className={category === c.id ? 'on' : ''} onClick={() => setCategory(c.id)}>
              <i className={`fa ${c.icon}`} /> {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="dcx-store-grid">
        {shown.map((t) => {
          const existing = added[t.id];
          return (
            <article key={t.id} className={`dcx-app ${existing ? 'dcx-app--added' : ''}`} aria-label={t.name}>
              <div className="dcx-app-top">
                <div className="dcx-app-icon"><i className={`fa ${t.icon}`} /></div>
                <div className="dcx-app-name">
                  <b>{t.name}</b>
                  <span>{t.provider}</span>
                </div>
              </div>
              <p className="dcx-app-tagline">{t.tagline}</p>
              <div className="dcx-app-badges">
                <span className="dcx-badge dcx-badge--free">{t.keyRequired ? 'Free · free key' : 'Free · no key'}</span>
                <span className="dcx-badge"><i className="fa fa-earth-europe" /> {t.coverage}</span>
              </div>
              <ul className="dcx-app-ideas">
                {t.outreachIdeas.map((idea) => <li key={idea}>{idea}</li>)}
              </ul>
              <div className="dcx-app-needs">
                <i className="fa fa-user" /> needs {t.needs.map((k) => CONTACT_INPUTS[k] || k).join(' + ')}
              </div>
              <div className="dcx-app-foot">
                <a className="dcx-link" href={t.docsUrl} target="_blank" rel="noopener noreferrer">Docs</a>
                {existing ? (
                  <button type="button" className="altBtn altBtn--petrol" onClick={() => onOpen(existing)}>
                    <i className="fa fa-check" /> Added · open
                  </button>
                ) : (
                  <button type="button" className="altBtn altBtn--petrol" onClick={() => onAdd(t)}>
                    <i className="fa fa-plus" /> Add
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ── List ────────────────────────────────────────────────────────────────────

const DataConnectorsSettings = ({ userId }) => {
  const { connectors, loading } = useDataConnectors(userId);
  const { customFields } = useCustomFields(userId);
  const { research, researchLoading, researchContext } = useResearchAccess();
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(null);
  const [deleting, setDeleting] = useState('');
  const identityState = useConnectorIdentity(userId);

  if (!researchLoading && research[DATA_CONNECTOR_LICENSE] !== true) {
    return (
      <div className="dcx">
        <ResearchLockedNotice featureName="Custom Researchers" accountEmail={researchContext.accountEmail} worldId={researchContext.worldId} />
      </div>
    );
  }

  if (editing || adding) {
    return (
      <div className="dcx">
        <ConnectorWizard
          key={adding?.id || editing?.id || 'new'}
          userId={userId}
          connector={editing && editing !== 'new' ? editing : null}
          template={adding}
          senderAgent={requesterUserAgent(identityState.identity)}
          onClose={() => { setEditing(null); setAdding(null); }}
        />
      </div>
    );
  }

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Its API key is deleted too. Data already saved on contacts stays.`)) return;
    setDeleting(c.id);
    try {
      await callDataConnector(userId, 'delete', { connectorId: c.id });
    } catch (err) {
      window.alert(err.message);
    } finally {
      setDeleting('');
    }
  };

  return (
    <div className="dcx">
      <div className="dcx-intro">
        <div>
          <h3>Custom researchers</h3>
          <p>
            Connect any platform that has an API — project databases, registries, industry tools. Each data source
            becomes a researcher next to Web Research: run it from Research on selected contacts or as a step in an
            agent, and its results flow into your emails.
          </p>
        </div>
        <button type="button" className="altBtn altBtn--petrol" onClick={() => setEditing('new')}>
          <i className="fa fa-plus" /> New data source
        </button>
      </div>

      <SenderIdentity identityState={identityState} />

      {loading && <div className="dcx-muted"><i className="fa fa-spinner fa-spin" /> Loading…</div>}
      {!loading && !connectors.length && (
        <div className="dcx-empty">
          <i className="fa fa-plug" />
          <span>No data sources yet. Start with a free one from the store below, or connect your own platform — the assistant reads the API docs for you.</span>
        </div>
      )}

      <div className="dcx-list">
        {connectors.map((c) => (
          <div key={c.id} className={`dcx-card dcx-card--${c.status || 'active'}`}>
            <div className="dcx-card-icon"><i className={`fa ${c.icon || 'fa-plug'}`} /></div>
            <div className="dcx-card-body">
              <div className="dcx-card-title">
                <span>{c.name}</span>
                <span className={`dcx-status dcx-status--${c.status || 'active'}`}>{c.status === 'error' ? 'Needs attention' : 'Active'}</span>
              </div>
              {c.goal && <p className="dcx-card-goal">{c.goal}</p>}
              <div className="dcx-card-meta">
                <span><i className="fa fa-globe" /> {c.host}</span>
                {(c.inputs || []).length > 0 && <span><i className="fa fa-user" /> sends {(c.inputs || []).map((k) => inputLabel(k, customFields)).join(', ')}</span>}
                {c.auth?.type !== 'none' && <span><i className="fa fa-key" /> {c.hasKey ? c.keyPreview : 'no key'}</span>}
                <span><i className="fa fa-clock" /> last run {timeAgo(c.lastRunAt)}</span>
                {c.stats && <span><i className="fa fa-chart-simple" /> {c.stats.ok || 0} ok · {c.stats.failed || 0} failed</span>}
              </div>
              {c.status === 'error' && c.lastError && (
                <div className="dcx-card-error">
                  <i className="fa fa-circle-exclamation" />
                  <span><b>{reasonLabel(c.lastError.reason)}</b> — {c.lastError.message} <span className="dcx-muted">({timeAgo(c.lastError.at)})</span></span>
                </div>
              )}
            </div>
            <div className="dcx-card-actions">
              <button type="button" className="dcx-icon-btn" onClick={() => setEditing(c)} title="Edit & test"><i className="fa fa-pen" /></button>
              <button type="button" className="dcx-icon-btn" onClick={() => remove(c)} disabled={deleting === c.id} title="Delete">
                <i className={`fa ${deleting === c.id ? 'fa-spinner fa-spin' : 'fa-trash'}`} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {!loading && <SourceStore connectors={connectors} onAdd={setAdding} onOpen={setEditing} />}
    </div>
  );
};

export default DataConnectorsSettings;
